import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import { isLoggedIn, getCurrentUser } from '../services/authService';

interface Props {
  onClose: () => void;
}

const ReferralModal: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const [targetMobile, setTargetMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [myCode, setMyCode] = useState('');
  const [loadingCode, setLoadingCode] = useState(false);

  useEffect(() => {
    if (!isLoggedIn()) return;
    const user = getCurrentUser();
    if (!user) return;
    setLoadingCode(true);
    fetch(`${API_BASE_URL}/Referral/code?mobile=${user.phoneNumber}`)
      .then(r => r.json())
      .then(data => { if (data.isSuccess) setMyCode(data.code); })
      .catch(() => {})
      .finally(() => setLoadingCode(false));
  }, []);

  const handleSend = async () => {
    if (!targetMobile.trim() || targetMobile.length < 10) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    const user = getCurrentUser();
    if (!user) { setError('Please login first.'); return; }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/Referral/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referrerMobile: user.phoneNumber,
          targetMobile: `91${targetMobile.replace(/^0/, '')}`,
        }),
      });
      const data = await res.json();
      if (data.isSuccess) setSent(true);
      else setError(data.message || 'Failed to send. Try again.');
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-lg rounded-t-2xl p-6 pb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Refer &amp; Earn</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="fas fa-times text-xl" />
          </button>
        </div>

        <div className="flex gap-3 mb-5">
          <div className="flex-1 bg-purple-50 rounded-xl p-3 text-center border border-purple-100">
            <div className="text-2xl font-bold text-purple-600">₹150</div>
            <div className="text-xs text-gray-500 mt-0.5">Your friend saves</div>
          </div>
          <div className="flex-1 bg-yellow-50 rounded-xl p-3 text-center border border-yellow-100">
            <div className="text-2xl font-bold text-yellow-500">₹100</div>
            <div className="text-xs text-gray-500 mt-0.5">You earn</div>
          </div>
        </div>

        {isLoggedIn() && (
          <div className="mb-4 bg-gray-50 rounded-xl p-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 mb-0.5">Your referral code</div>
              <div className="font-mono font-bold text-gray-900 text-lg tracking-widest">
                {loadingCode ? '...' : (myCode || '—')}
              </div>
            </div>
            {myCode && (
              <button onClick={() => navigator.clipboard.writeText(myCode)} className="text-purple-500 hover:text-purple-700" title="Copy">
                <i className="fas fa-copy text-lg" />
              </button>
            )}
          </div>
        )}

        {!isLoggedIn() && (
          <div className="flex items-center justify-between bg-amber-50 rounded-xl p-3 mb-4">
            <p className="text-sm text-amber-600">
              <i className="fas fa-info-circle mr-1" />
              Login to get your referral code.
            </p>
            <button
              onClick={() => { onClose(); navigate('/login'); }}
              className="text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-lg ml-2 whitespace-nowrap"
            >
              Click here to Login
            </button>
          </div>
        )}

        <label className="text-sm font-medium text-gray-700 block mb-1.5">
          Send code to a friend via WhatsApp
        </label>
        <div className="flex gap-2 mb-1">
          <div className="flex items-center border border-gray-200 rounded-lg px-3 bg-gray-50 text-sm text-gray-500">+91</div>
          <input
            type="tel"
            value={targetMobile}
            onChange={e => { setTargetMobile(e.target.value.replace(/\D/g, '')); setError(''); }}
            placeholder="Friend's mobile number"
            maxLength={10}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-purple-400"
          />
        </div>
        {error && <p className="text-xs text-red-500 mt-1 mb-1">{error}</p>}

        {sent ? (
          <div className="mt-3 flex items-center gap-2 text-green-700 bg-green-50 rounded-xl p-3">
            <i className="fas fa-check-circle" />
            <span className="text-sm font-medium">WhatsApp sent to your friend!</span>
          </div>
        ) : (
          <button
            onClick={handleSend}
            disabled={sending || !isLoggedIn()}
            className="mt-3 w-full py-3 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2 disabled:bg-gray-300"
            style={sending || !isLoggedIn() ? {} : { background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
          >
            {sending
              ? <><i className="fas fa-spinner fa-spin" /> Sending...</>
              : <><i className="fab fa-whatsapp text-lg" /> Send via WhatsApp</>}
          </button>
        )}
      </div>
    </div>
  );
};

export default ReferralModal;
